/**
 * NewTrainerScorecardPage — /training/new-scorecard
 *
 * Three-pillar Trainer Performance Score:
 *   🎯 Objective (25%) · 📋 Operational (50%) · 💬 Quality (25%)
 *
 * Layout:
 *   • Header + period selector (Monthly/Quarterly/Yearly)
 *   • Stat strip (avg TPS, fully-scored count, baseline median load)
 *   • Pillar/grade legend
 *   • Leaderboard with 3 pillar columns + coverage badge + flags
 *   • Click row → drill-down: 3 pillar cards (each with its own components)
 *     + 3-period trend bars + manager input form for Quality + flags + audit trail
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  Trophy, Calendar, RefreshCw, AlertTriangle, ChevronRight, ChevronDown, Save, X,
  TrendingUp, TrendingDown, Minus, Info, Target, ClipboardList, MessageSquare,
  HelpCircle, BookOpen, Sparkles, Users, AlertCircle, Maximize2, ExternalLink,
  CheckCircle2, Crown, Award, ArrowDownToLine, FileText, UserX,
} from 'lucide-react';

/** Trigger a file download for any backend endpoint that returns a blob. */
async function downloadFile(path, filename) {
  try {
    const r = await api.get(path, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    if (typeof toast !== 'undefined') toast.error('Could not download file');
  }
}
import { toast } from 'sonner';
import api from '../../lib/api';
import BatchTopicCompletionDialog from './BatchTopicCompletionDialog';

const GRADE_STYLES = {
  Excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Good: 'bg-blue-100 text-blue-800 border-blue-200',
  Average: 'bg-amber-100 text-amber-800 border-amber-200',
  'Needs Improvement': 'bg-rose-100 text-rose-800 border-rose-200',
  'Not enough data': 'bg-slate-100 text-slate-600 border-slate-200',
};

const PILLAR_META = {
  objective:   { Icon: Target,        color: 'text-violet-600',  bg: 'bg-violet-50',  border: 'border-violet-200', label: 'Objective',   desc: 'Workload, completion, consistency, tenure' },
  operational: { Icon: ClipboardList, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Operational', desc: 'Working-day compliance, attendance & topic completion' },
  quality:     { Icon: MessageSquare, color: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-200',    label: 'Quality',     desc: 'Manager / student feedback, assessments' },
};

// Friendly full-name map for every component key. Used everywhere keys are rendered.
const COMPONENT_LABELS = {
  // Objective pillar
  workload:     { name: 'Workload',                short: 'Workload',           help: 'Active students this trainer is handling right now (in running batches) vs the org median. Capped at 100.' },
  completion:   { name: 'Batch Completion Rate',   short: 'Completion',         help: 'Of the batches the trainer was supposed to deliver (Completed + any overrunning past end date), what % were actually completed. Cancelled / On-Hold batches are excluded — those are business decisions, not trainer-led.' },
  consistency:  { name: 'Consistency',             short: 'Consistency',        help: 'How many distinct months in the last 12 months they ran at least one batch.' },
  versatility:  { name: 'Course Versatility',      short: 'Versatility',        help: 'Number of distinct courses taught (capped at 5+ courses = 100).' },
  tenure_bonus: { name: 'Tenure Bonus',            short: 'Tenure',             help: 'Years on the platform, capped at 5 years = 100.' },
  // Operational pillar
  working_day:  { name: 'Working-Day Compliance',  short: 'Working Days',       help: 'Of the trainer\'s expected sessions in this period (trainer working_days × each batch\'s own schedule — Weekday batches on Mon-Thu, Weekend batches on Sat-Sun — × past dates), what % were actually held. Missing submissions count as Missed. Holidays / Centre Closures / Approved Leaves are excluded from the denominator.' },
  attendance:   { name: 'Student Attendance Rate', short: 'Attendance',         help: 'Session-average across every expected session in the period. Each held session contributes its (Present+Late) / Total %; missed-or-unsubmitted sessions contribute 0; excused (Holiday/Closure/Leave) are skipped. So a trainer who skips submission can\'t hide behind a single 100 % session.' },
  cct:          { name: 'Course Completion Timeliness',  short: 'Topic Completion',   help: 'Of the syllabus subtopics, what % have been ticked off as completed by the trainer.' },
  // Quality pillar
  tdq:          { name: 'Training Delivery Quality',     short: 'Delivery Quality',   help: 'Manager observation rating, scale 0–5. Rates how well the trainer delivers the session content.' },
  sfs:          { name: 'Student Feedback Score',         short: 'Student Feedback',   help: 'Average student feedback rating, scale 0–5. Once the public feedback form is built, this auto-populates.' },
  ras:          { name: 'Review / Assessment Score',      short: 'Assessment',         help: 'Avg score (%) of student tests/assignments under this trainer.' },
};

// Glossary entries shown in the help panel. Every entry below maps to
// something the user will actually see on this page (column header,
// pillar component label, badge, or stat-card title).
const GLOSSARY = [
  { abbr: 'TPS',         full: 'Trainer Performance Score',       desc: 'The final 0–100 number for a trainer in the selected period. A weighted blend of three pillars (Objective 25% · Operational 50% · Quality 25%), auto-renormalised when a pillar has no data.' },
  { abbr: 'Grade',       full: 'Performance Grade',               desc: 'TPS bucket: Excellent ≥ 90 · Good 75–89 · Average 60–74 · Needs Improvement < 60.' },
  { abbr: 'Coverage',    full: 'Pillar Coverage %',               desc: 'How much of the full TPS we could measure for this trainer. 100% means all 3 pillars had data; lower means some pillars were skipped.' },
  { abbr: 'Workload',    full: 'Active Student Load',             desc: 'Active students this trainer handles right now (in running batches) ÷ org median. Capped at 100 so over-loaded trainers aren\'t penalised. Weight 40% inside Objective.' },
  { abbr: 'Completion',  full: 'Batch Completion Rate',           desc: '% of trainer-controlled batches that reached Completed. Denominator = Completed + overdue-unfinished. Cancelled / On-Hold batches are excluded as business decisions. Newcomers get a neutral 50 baseline. Weight 20% inside Objective.' },
  { abbr: 'Consistency', full: 'Months Active',                   desc: 'Distinct months in the last 12 with at least one batch. Weight 10% inside Objective.' },
  { abbr: 'Versatility', full: 'Course Versatility',              desc: 'Distinct courses taught (5+ courses = 100). Weight 25% inside Objective.' },
  { abbr: 'Tenure',      full: 'Tenure Bonus',                    desc: '1 year on the platform = 50, 2+ years = 100. Weight 5% inside Objective.' },
  { abbr: 'Working Day', full: 'Working-Day Compliance',          desc: 'Of the trainer\'s expected sessions in the period (trainer working_days × each batch\'s own schedule — Weekday batches on Mon-Thu, Weekend batches on Sat-Sun — × past dates), % actually held. Missing submissions count as Missed. Holidays / Centre Closures / Approved Leaves are excluded from the denominator. Weight 40% inside Operational.' },
  { abbr: 'Attendance',  full: 'Student Attendance Rate',         desc: 'Session-average across every expected session in the period. Each held session contributes (Present+Late)/Total %, missed-or-unsubmitted sessions contribute 0, excused (Holiday/Closure/Leave) are skipped. Weight 25% inside Operational.' },
  { abbr: 'CCT',         full: 'Course Completion Timeliness',    desc: 'Of the syllabus subtopics for a batch, % ticked off by the trainer. Pulled from the Course Syllabus + Topic Completion modules. Weight 35% inside Operational.' },
  { abbr: 'TDQ',         full: 'Training Delivery Quality',       desc: 'Manager rating of how well the trainer delivers content (0–5). Manual entry today; will move to recorded-session review later. Weight 40% inside Quality.' },
  { abbr: 'SFS',         full: 'Student Feedback Score',          desc: 'Average rating from students (0–5). Manual today; will auto-feed once the public feedback form is launched. Weight 35% inside Quality.' },
  { abbr: 'RAS',         full: 'Recorded Assessment Score',       desc: '% score from tests & assignments. Manual today; auto once assessment results are imported. Weight 25% inside Quality.' },
  { abbr: 'Placement',   full: 'Placement Rate (display only)',   desc: '% of students placed within 90 days of batch end. Shown on the trainer\'s drilldown for context — does NOT affect TPS.' },
  { abbr: 'Flags',       full: 'Auto-detected flags',             desc: 'Lightweight alerts surfaced by the system, e.g. low attendance, lagging course completion, or no-data periods. Useful for the manager review meeting.' },
];

function buildPeriodOptions(periodType) {
  const today = new Date();
  const opts = [];
  if (periodType === 'month') {
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      opts.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleString('en', { month: 'short', year: 'numeric' }) });
    }
  } else if (periodType === 'quarter') {
    let y = today.getFullYear(), q = Math.floor(today.getMonth() / 3) + 1;
    for (let i = 0; i < 8; i++) {
      opts.push({ key: `${y}-Q${q}`, label: `Q${q} ${y}` });
      q -= 1;
      if (q < 1) { q = 4; y -= 1; }
    }
  } else {
    for (let i = 0; i < 4; i++) opts.push({ key: String(today.getFullYear() - i), label: String(today.getFullYear() - i) });
  }
  return opts;
}

function PillarBadge({ score, name, coverage }) {
  const meta = PILLAR_META[name];
  const Icon = meta.Icon;
  return (
    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${meta.bg} ${meta.color} border ${meta.border}`} title={meta.desc}>
      <Icon className="w-2.5 h-2.5" />
      <span className="font-semibold tabular-nums">{score === null ? '—' : score}</span>
      {coverage === false && <span className="text-[8px] italic opacity-60">no data</span>}
    </div>
  );
}

// Auto-generates a plain-English interpretation of the current period's snapshot.
function InterpretationCard({ data, periodType, periodKey }) {
  if (!data?.rows?.length) return null;

  const rows = data.rows;
  const scored = rows.filter(r => r.tps !== null);
  const avg = data.avg_tps;
  const total = data.trainer_count;

  // Top performer
  const top = scored[0]; // rows already sorted desc by tps

  // Lowest scorer (excluding nulls)
  const bottom = scored.length > 1 ? scored[scored.length - 1] : null;

  // Highest-flag trainer
  const mostFlags = [...rows].sort((a, b) => (b.flags?.length || 0) - (a.flags?.length || 0))[0];
  const flagCount = mostFlags?.flags?.length || 0;

  // How many need Quality scores
  const needQuality = rows.filter(r => r.quality?.score === null).length;

  // Coverage gap — pillar most often missing
  const missingByPillar = { objective: 0, operational: 0, quality: 0 };
  rows.forEach(r => {
    if (r.objective?.score === null) missingByPillar.objective++;
    if (r.operational?.score === null) missingByPillar.operational++;
    if (r.quality?.score === null) missingByPillar.quality++;
  });
  const biggestGap = Object.entries(missingByPillar).sort((a, b) => b[1] - a[1])[0];

  // Excellence vs concerns count
  const excellent = scored.filter(r => r.tps >= 90).length;
  const good = scored.filter(r => r.tps >= 75 && r.tps < 90).length;
  const average = scored.filter(r => r.tps >= 60 && r.tps < 75).length;
  const needsHelp = scored.filter(r => r.tps < 60).length;

  // Pretty period label
  const periodLabel =
    periodType === 'month' ? `this month (${periodKey})` :
    periodType === 'quarter' ? `this quarter (${periodKey})` :
    `this year (${periodKey})`;

  // Headline tone
  let headlineEmoji = '📊';
  let headlineText = `Mixed picture ${periodLabel}.`;
  if (avg >= 80) { headlineEmoji = '🌟'; headlineText = `Strong overall performance ${periodLabel}.`; }
  else if (avg >= 70) { headlineEmoji = '✅'; headlineText = `Healthy team ${periodLabel} with a few action areas.`; }
  else if (avg >= 60) { headlineEmoji = '⚠️'; headlineText = `Average performance ${periodLabel} — meaningful improvement possible.`; }
  else if (avg !== null) { headlineEmoji = '🔴'; headlineText = `Team is under-performing ${periodLabel} — immediate attention needed.`; }

  return (
    <Card className="p-4 bg-gradient-to-br from-violet-50/80 via-white to-indigo-50/40 border-violet-200" data-testid="new-scorecard-interpretation">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Score Interpretation</p>
            <span className="text-[9px] text-slate-400">· auto-generated from live data</span>
          </div>

          <p className="text-sm text-slate-800 mb-2">
            <span className="text-base mr-1">{headlineEmoji}</span>
            <strong>{headlineText}</strong>
            {avg !== null && (
              <> Team average <strong className="text-violet-700">TPS {avg}</strong> across <strong>{total}</strong> active trainers.</>
            )}
          </p>

          {/* Distribution mini-bar */}
          <div className="flex items-center gap-1 mb-3">
            <span className="text-[10px] text-slate-500 mr-1">Distribution:</span>
            {excellent > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold">🏆 {excellent} Excellent</span>
            )}
            {good > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-semibold">✅ {good} Good</span>
            )}
            {average > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">⚠️ {average} Average</span>
            )}
            {needsHelp > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px] font-semibold">🔴 {needsHelp} Need Improvement</span>
            )}
          </div>

          {/* Insight bullets */}
          <ul className="space-y-1 text-[12px] text-slate-700">
            {top && (
              <li className="flex items-start gap-2">
                <Trophy className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{top.trainer_name.split('.')[0]}</strong> is your strongest performer at{' '}
                  <strong className="text-emerald-700">TPS {top.tps}</strong>{' '}
                  ({top.grade.label}). They scored{' '}
                  <strong>{top.objective?.score ?? '—'}</strong> on Objective and{' '}
                  <strong>{top.operational?.score ?? '—'}</strong> on Operational.
                </span>
              </li>
            )}
            {flagCount >= 2 && mostFlags && (
              <li className="flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{mostFlags.trainer_name.split('.')[0]}</strong> has the most flags ({flagCount}):{' '}
                  <em className="text-rose-700">{mostFlags.flags.slice(0, 2).join('; ')}{flagCount > 2 ? '…' : ''}</em>
                </span>
              </li>
            )}
            {bottom && bottom.tps !== null && bottom.tps < 60 && bottom !== top && (
              <li className="flex items-start gap-2">
                <TrendingDown className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{bottom.trainer_name.split('.')[0]}</strong> is the lowest at{' '}
                  <strong className="text-rose-700">TPS {bottom.tps}</strong> — schedule a 1:1 review.
                </span>
              </li>
            )}
            {needQuality > 0 && (
              <li className="flex items-start gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{needQuality} of {total} trainers</strong> need their <strong>Quality scores</strong> entered (TDQ / SFS / RAS) — click any row to add them and lift their coverage to 100 %.
                </span>
              </li>
            )}
            {biggestGap && biggestGap[1] > 0 && biggestGap[0] !== 'quality' && (
              <li className="flex items-start gap-2">
                <Users className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  Biggest data gap: <strong>{biggestGap[1]} trainers</strong> have no <strong>{PILLAR_META[biggestGap[0]].label}</strong> data this period
                  {biggestGap[0] === 'operational' && ' — nudge trainers to submit daily attendance & tick syllabus topics'}.
                </span>
              </li>
            )}
            {needsHelp === 0 && excellent + good >= total / 2 && (
              <li className="flex items-start gap-2">
                <span className="text-base flex-shrink-0">🎉</span>
                <span>No trainer is in the "Needs Improvement" bracket — solid baseline across the team.</span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </Card>
  );
}

export default function NewTrainerScorecardPage() {
  const [periodType, setPeriodType] = useState('month');
  const [periodOptions, setPeriodOptions] = useState(buildPeriodOptions('month'));
  const [periodKey, setPeriodKey] = useState(periodOptions[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillTrainer, setDrillTrainer] = useState(null);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  useEffect(() => {
    const opts = buildPeriodOptions(periodType);
    setPeriodOptions(opts);
    setPeriodKey(opts[0].key);
  }, [periodType]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/new-scorecard', { params: { period_type: periodType, period_key: periodKey } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [periodType, periodKey]);

  const top = useMemo(() => data?.rows?.find(r => r.tps !== null), [data]);

  return (
    <div className="p-6 space-y-4" data-testid="new-trainer-scorecard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center shadow">
            <Trophy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              New Trainer Scorecard
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-bold">v2 · 3-Pillar TPS</span>
            </h1>
            <p className="text-sm text-slate-500">
              Each trainer gets a <strong className="text-slate-700">Trainer Performance Score (TPS)</strong> blending three pillars:{' '}
              <span className="font-semibold text-violet-700">🎯 Objective</span> 25 % ·
              <span className="font-semibold text-emerald-700"> 📋 Operational</span> 50 % ·
              <span className="font-semibold text-rose-700"> 💬 Quality</span> 25 %.
              <span className="text-slate-400"> (Placement rate shown for context.)</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setGlossaryOpen(o => !o)} data-testid="new-scorecard-glossary-toggle">
            <HelpCircle className="w-3.5 h-3.5 mr-1" /> {glossaryOpen ? 'Hide Glossary' : 'What do these mean?'}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="new-scorecard-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Glossary popup */}
      <Dialog open={glossaryOpen} onOpenChange={setGlossaryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="new-scorecard-glossary-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-violet-600" /> Trainer Scorecard — Glossary &amp; Calculation Guide
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-1">Every abbreviation on this page, plus a worked example of how the TPS is computed. Share this with management.</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadFile(`/training/new-scorecard/glossary.pdf`, 'TPS_Glossary.pdf')}
                className="text-xs"
                data-testid="glossary-download-pdf"
              >
                <ArrowDownToLine className="w-3.5 h-3.5 mr-1" /> Download PDF
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-3">
          <p className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-3 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Glossary — every abbreviation on this page
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[11px]">
            {GLOSSARY.map(g => (
              <div key={g.abbr} className="flex gap-2">
                <div className="flex-shrink-0 w-24">
                  <span className="px-1.5 py-0.5 bg-violet-100 text-violet-800 rounded font-mono font-bold">{g.abbr}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{g.full}</p>
                  <p className="text-slate-600 leading-relaxed">{g.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 italic mt-3 pt-2 border-t border-violet-200">
            Hover any score, badge, or column header on this page for a quick tooltip with the full name.
          </p>

          {/* Worked example so anyone can understand what's happening */}
          <div className="mt-4 pt-4 border-t border-violet-200 space-y-3">
            <p className="text-[11px] uppercase tracking-wider font-bold text-violet-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> How TPS is calculated — a worked example
            </p>
            <div className="bg-white border border-slate-200 rounded p-3 space-y-3 text-[11px] leading-relaxed">
              <p className="text-slate-700">
                <strong>Sample trainer "Aanya"</strong> for this month. We score her on three pillars; each pillar is the weighted average of its components.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Objective */}
                <div className="bg-violet-50 border border-violet-200 rounded p-2 space-y-1">
                  <p className="font-bold text-violet-800 text-xs">🎯 Objective (25% of TPS)</p>
                  <table className="w-full text-[10px]">
                    <tbody>
                      <tr><td>Workload</td><td className="text-right tabular-nums">80</td><td className="text-right text-slate-500">×40%</td></tr>
                      <tr><td>Completion</td><td className="text-right tabular-nums">90</td><td className="text-right text-slate-500">×20%</td></tr>
                      <tr><td>Consistency</td><td className="text-right tabular-nums">70</td><td className="text-right text-slate-500">×10%</td></tr>
                      <tr><td>Versatility</td><td className="text-right tabular-nums">60</td><td className="text-right text-slate-500">×25%</td></tr>
                      <tr><td>Tenure</td><td className="text-right tabular-nums">100</td><td className="text-right text-slate-500">×5%</td></tr>
                    </tbody>
                  </table>
                  <div className="border-t pt-1 text-[10px] text-violet-800 font-mono">
                    32 + 18 + 7 + 15 + 5 = <strong>77</strong>
                  </div>
                </div>

                {/* Operational */}
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 space-y-1">
                  <p className="font-bold text-emerald-800 text-xs">📋 Operational (50% of TPS)</p>
                  <table className="w-full text-[10px]">
                    <tbody>
                      <tr><td>Working Days</td><td className="text-right tabular-nums">95</td><td className="text-right text-slate-500">×40%</td></tr>
                      <tr><td>Attendance</td><td className="text-right tabular-nums">85</td><td className="text-right text-slate-500">×25%</td></tr>
                      <tr><td>Topic Comp.</td><td className="text-right tabular-nums">70</td><td className="text-right text-slate-500">×35%</td></tr>
                    </tbody>
                  </table>
                  <div className="border-t pt-1 text-[10px] text-emerald-800 font-mono">
                    38 + 21.25 + 24.5 = <strong>83.75</strong>
                  </div>
                </div>

                {/* Quality */}
                <div className="bg-rose-50 border border-rose-200 rounded p-2 space-y-1">
                  <p className="font-bold text-rose-800 text-xs">💬 Quality (25% of TPS)</p>
                  <table className="w-full text-[10px]">
                    <tbody>
                      <tr><td>Delivery (TDQ)</td><td className="text-right tabular-nums">90</td><td className="text-right text-slate-500">×40%</td></tr>
                      <tr><td>Feedback (SFS)</td><td className="text-right tabular-nums">80</td><td className="text-right text-slate-500">×35%</td></tr>
                      <tr><td>Assessment (RAS)</td><td className="text-right tabular-nums">75</td><td className="text-right text-slate-500">×25%</td></tr>
                    </tbody>
                  </table>
                  <div className="border-t pt-1 text-[10px] text-rose-800 font-mono">
                    36 + 28 + 18.75 = <strong>82.75</strong>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-2 space-y-1">
                <p className="font-bold text-slate-700 text-xs">Final TPS = pillar-weighted average</p>
                <p className="font-mono text-[11px] text-slate-700">
                  TPS = (77 × <strong>0.25</strong>) + (84.5 × <strong>0.50</strong>) + (82.75 × <strong>0.25</strong>)
                </p>
                <p className="font-mono text-[11px] text-slate-700">
                  TPS = 38.5 + 25.35 + 16.55 = <strong className="text-violet-700 text-sm">80.4</strong> → Grade: <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">Good</Badge>
                </p>
              </div>

              <ul className="text-[10px] text-slate-600 space-y-1 list-disc list-inside">
                <li><strong>Auto-renormalisation:</strong> if a pillar has no data (e.g. Quality wasn't entered yet), its weight is redistributed to the others so trainers aren't punished for missing-data they don't control.</li>
                <li><strong>Newcomer baselines:</strong> Completion defaults to a neutral 50 if the trainer has no eligible batches yet — so brand-new trainers don't appear as failing.</li>
                <li><strong>Click any component bar above</strong> in a trainer's drill-down to see exactly which batches / sessions / topics fed into the score.</li>
              </ul>
            </div>

            <p className="text-[11px] uppercase tracking-wider font-bold text-violet-700 flex items-center gap-1.5 pt-2">
              <Info className="w-3.5 h-3.5" /> What makes each component go up?
            </p>
            <div className="bg-white border border-slate-200 rounded p-3 text-[11px] leading-relaxed">
              <ul className="space-y-1.5 list-disc list-inside text-slate-700">
                <li><strong>Workload</strong> — bring more active students relative to the org median.</li>
                <li><strong>Completion</strong> — finish your batches on time. Cancelled / On-Hold batches are excluded (business decisions, not trainer-led). Batches past their planned end-date but still showing as Running pull this score down.</li>
                <li><strong>Consistency</strong> — be active in as many distinct months as possible across the last 12 months.</li>
                <li><strong>Versatility</strong> — teach a broader range of distinct courses (capped at 5+ courses = 100).</li>
                <li><strong>Tenure</strong> — automatic; 1 year on the platform = 50, 2+ years = 100.</li>
                <li><strong>Working Days</strong> — every Held session = good. Excused leaves don't count against you. Missed sessions without a valid reason hurt this.</li>
                <li><strong>Attendance</strong> — students showing up = good. Engage them so they don't go absent.</li>
                <li><strong>Topic Completion</strong> — tick off subtopics in the linked syllabus as you finish them. Pace should match how far through the calendar you are.</li>
                <li><strong>Quality (TDQ / SFS / RAS)</strong> — manager-entered for now. Will be wired to the student-feedback form and the assessment module in a later release.</li>
              </ul>
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Period selector */}
      <Card className="p-3 flex items-center gap-3 flex-wrap">
        <Calendar className="w-4 h-4 text-slate-500" />
        <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Period:</span>
        <Select value={periodType} onValueChange={setPeriodType}>
          <SelectTrigger className="w-[140px] h-9" data-testid="new-scorecard-period-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Monthly</SelectItem>
            <SelectItem value="quarter">Quarterly</SelectItem>
            <SelectItem value="year">Yearly</SelectItem>
          </SelectContent>
        </Select>
        <Select value={periodKey} onValueChange={setPeriodKey}>
          <SelectTrigger className="w-[180px] h-9" data-testid="new-scorecard-period-key"><SelectValue /></SelectTrigger>
          <SelectContent>{periodOptions.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        {data && (
          <span className="ml-auto flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span><strong className="text-slate-700">{data.fully_scored}</strong> of {data.trainer_count} fully scored</span>
            {data.avg_tps !== null && <span title="Average Trainer Performance Score across all trainers in this period">Avg <strong className="text-slate-700">TPS</strong>: <strong className="text-slate-700">{data.avg_tps}</strong></span>}
            {top && top.tps !== null && (
              <span>🏆 Top: <strong className="text-slate-700">{top.trainer_name.split('.')[0]}</strong> ({top.tps})</span>
            )}
            <span>Org median load: <strong className="text-slate-700">{data.baselines?.median_active_load}</strong> students</span>
          </span>
        )}
      </Card>

      {/* Score Interpretation — auto-generated plain-English snapshot */}
      {data && <InterpretationCard data={data} periodType={periodType} periodKey={periodKey} />}

      {/* Highlight cards — most/least students, most/least batches */}
      {data && data.highlights && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="highlight-cards">
          <HighlightCard tone="emerald" Icon={Crown}    label="Most Active Students"  pick={data.highlights.max_students} suffix="students" onClick={(t) => setDrillTrainer(data.rows.find(r => r.trainer_id === t))} />
          <HighlightCard tone="rose"    Icon={Users}    label="Lowest Active Students" pick={data.highlights.min_students} suffix="students" onClick={(t) => setDrillTrainer(data.rows.find(r => r.trainer_id === t))} />
          <HighlightCard tone="violet"  Icon={Trophy}   label="Most Active Batches"   pick={data.highlights.max_batches}  suffix="batches"  onClick={(t) => setDrillTrainer(data.rows.find(r => r.trainer_id === t))} />
          <HighlightCard tone="amber"   Icon={Award}    label="Lowest Active Batches" pick={data.highlights.min_batches}  suffix="batches"  onClick={(t) => setDrillTrainer(data.rows.find(r => r.trainer_id === t))} />
        </div>
      )}

      {/* Pillar legend with full names */}
      <Card className="p-3 bg-slate-50/60 border-slate-200">
        <div className="flex items-start gap-3 text-[11px]">
          <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="space-y-2 flex-1">
            <p>
              <strong className="text-slate-700">TPS</strong> (Trainer Performance Score) blends three pillars; weights auto-renormalise across whichever pillars have data:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="flex items-start gap-2 px-2 py-1.5 bg-violet-50 border border-violet-200 rounded">
                <Target className="w-3.5 h-3.5 text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-violet-800">🎯 Objective <span className="text-[10px] font-normal text-violet-600">(25 %)</span></p>
                  <p className="text-slate-600 leading-relaxed">Workload, Completion, Consistency, Versatility, Tenure</p>
                </div>
              </div>
              <div className="flex items-start gap-2 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded">
                <ClipboardList className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">📋 Operational <span className="text-[10px] font-normal text-emerald-600">(50 %)</span></p>
                  <p className="text-slate-600 leading-relaxed">Working-Day Compliance 40 · Attendance 25 · Topic Completion 35</p>
                </div>
              </div>
              <div className="flex items-start gap-2 px-2 py-1.5 bg-rose-50 border border-rose-200 rounded">
                <MessageSquare className="w-3.5 h-3.5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-rose-800">💬 Quality <span className="text-[10px] font-normal text-rose-600">(25 %)</span></p>
                  <p className="text-slate-600 leading-relaxed">TDQ (Delivery /5), SFS (Feedback /5), RAS (Assessment %)</p>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 pt-0.5">
              <Trophy className="w-3 h-3 text-amber-600" />
              <strong className="text-slate-600">Placement Rate</strong> is shown on each trainer for context (not weighted into TPS).
            </p>
            <p className="pt-1">
              <strong className="text-slate-700">Grading:</strong>{' '}
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mr-1">🏆 Excellent ≥90</span>
              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 mr-1">✅ Good 75–89</span>
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-1">⚠️ Average 60–74</span>
              <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">🔴 Needs Improvement &lt;60</span>
            </p>
          </div>
        </div>
      </Card>

      {/* Leaderboard */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="new-scorecard-table">
            <thead className="bg-slate-50 border-b text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">#</th>
                <th className="text-left px-3 py-2 font-semibold">Trainer</th>
                <th className="text-right px-3 py-2 font-semibold" title="Trainer Performance Score (0–100), weighted across all 3 pillars">TPS <HelpCircle className="w-2.5 h-2.5 inline text-slate-300" /></th>
                <th className="text-left px-3 py-2 font-semibold">Grade</th>
                <th className="text-center px-3 py-2 font-semibold" title="Score per pillar — Objective / Operational / Quality">Pillars <HelpCircle className="w-2.5 h-2.5 inline text-slate-300" /></th>
                <th className="text-right px-3 py-2 font-semibold" title="% of pillar weight measured. 100 % = all 3 pillars have data; lower = some inputs missing">Coverage <HelpCircle className="w-2.5 h-2.5 inline text-slate-300" /></th>
                <th className="text-left px-3 py-2 font-semibold" title="Number of issues automatically detected (drops, low attendance, weak feedback, etc.)">Flags <HelpCircle className="w-2.5 h-2.5 inline text-slate-300" /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!data && <tr><td colSpan={8} className="text-center py-12 text-slate-400">{loading ? 'Loading…' : 'No data'}</td></tr>}
              {data?.rows?.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-400">No active trainers</td></tr>}
              {data?.rows?.map((r, i) => (
                <tr key={r.trainer_name + i}
                    className="border-b hover:bg-violet-50/40 cursor-pointer transition-colors"
                    onClick={() => setDrillTrainer(r)}
                    data-testid={`new-scorecard-row-${i}`}>
                  <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.trainer_name}</td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums">
                    {r.tps !== null ? <span className="text-slate-800 text-sm">{r.tps}</span> : <span className="text-slate-300 italic">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`${GRADE_STYLES[r.grade.label] || ''} text-[10px] font-semibold`}>
                      {r.grade.emoji} {r.grade.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <PillarBadge score={r.objective?.score} name="objective" coverage={r.objective?.coverage} />
                      <PillarBadge score={r.operational?.score} name="operational" coverage={r.operational?.coverage} />
                      <PillarBadge score={r.quality?.score} name="quality" coverage={r.quality?.coverage} />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`text-[10px] font-bold tabular-nums ${
                      r.coverage.coverage_pct === 100 ? 'text-emerald-700' :
                      r.coverage.coverage_pct >= 80 ? 'text-amber-700' : 'text-rose-700'
                    }`}>{r.coverage.coverage_pct}%</span>
                  </td>
                  <td className="px-3 py-2">
                    {r.flags?.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-rose-600 text-[10px]" title={r.flags.join('\n')}>
                        <AlertTriangle className="w-3 h-3" /> {r.flags.length}
                      </span>
                    ) : <span className="text-emerald-500 text-[10px]">✓</span>}
                  </td>
                  <td className="px-3 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <DrillDown trainer={drillTrainer} periodType={periodType} periodKey={periodKey} onClose={() => setDrillTrainer(null)} onSaved={load} />
    </div>
  );
}

// ─── Drill-down ──────────────────────────────────────────────

function DrillDown({ trainer, periodType, periodKey, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(trainer);

  useEffect(() => {
    if (!trainer || !trainer.trainer_id) { setDetail(null); return; }
    setLoading(true);
    api.get(`/training/new-scorecard/trainer/${trainer.trainer_id}`, {
      params: { period_type: periodType, period_key: periodKey },
    }).then(r => setDetail(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || 'Failed to load detail'))
      .finally(() => setLoading(false));
  }, [trainer, periodType, periodKey]);

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-[720px] sm:max-w-[720px] overflow-y-auto p-0" data-testid="new-scorecard-drilldown">
        <SheetHeader className="px-5 py-4 border-b bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5" /> {trainer?.trainer_name || ''}
              </SheetTitle>
              <p className="text-xs opacity-90 mt-1">
                {periodType === 'month' ? 'Month' : periodType === 'quarter' ? 'Quarter' : 'Year'}: <strong>{periodKey}</strong>
              </p>
            </div>
            {trainer?.trainer_id && (
              <div className="flex flex-col gap-1.5 flex-shrink-0" data-testid="trainer-pdf-buttons">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => downloadFile(
                    `/training/new-scorecard/trainer/${trainer.trainer_id}/report.pdf?period_type=${periodType}&period_key=${periodKey}&detail=summary`,
                    `TPS_${(trainer.trainer_name || 'trainer').replace(/\W+/g, '_')}_${periodKey}_Summary.pdf`,
                  )}
                  className="h-7 px-2 text-[11px] bg-white text-violet-700 hover:bg-violet-50"
                  data-testid="download-summary-pdf"
                >
                  <ArrowDownToLine className="w-3 h-3 mr-1" />Summary PDF
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => downloadFile(
                    `/training/new-scorecard/trainer/${trainer.trainer_id}/report.pdf?period_type=${periodType}&period_key=${periodKey}&detail=full`,
                    `TPS_${(trainer.trainer_name || 'trainer').replace(/\W+/g, '_')}_${periodKey}_Detailed.pdf`,
                  )}
                  className="h-7 px-2 text-[11px] bg-white/90 text-indigo-700 hover:bg-white"
                  data-testid="download-detailed-pdf"
                >
                  <FileText className="w-3 h-3 mr-1" />Detailed PDF
                </Button>
              </div>
            )}
          </div>
        </SheetHeader>
        {loading && <div className="p-8 text-center text-sm text-slate-400">Loading…</div>}
        {detail && (
          <div className="p-5 space-y-5">
            {/* Aggregate */}
            <Card className="p-4">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold" title="Trainer Performance Score = weighted blend of Objective, Operational, and Quality pillars">
                  Final TPS — Trainer Performance Score
                </p>
                <Badge variant="outline" className={GRADE_STYLES[detail.row.grade.label] || ''}>
                  {detail.row.grade.emoji} {detail.row.grade.label}
                </Badge>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-slate-800 tabular-nums">{detail.row.tps ?? '—'}</span>
                <span className="text-slate-400 text-sm">/ 100</span>
                <span className="ml-auto text-[10px] text-slate-500">
                  Coverage <strong className="text-slate-700">{detail.row.coverage.coverage_pct}%</strong>
                  · pillars: {(detail.row.coverage.pillars_present || []).map(p => PILLAR_META[p].label).join(', ') || 'none'}
                </span>
              </div>
              {detail.row.flags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {detail.row.flags.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">
                      <AlertTriangle className="w-3 h-3" /> {f}
                    </span>
                  ))}
                </div>
              )}
            </Card>

            {/* Three pillar cards */}
            <div className="grid grid-cols-3 gap-3">
              <PillarCard pillar="objective"   data={detail.row.objective}   trainer={trainer} periodType={periodType} periodKey={periodKey} />
              <PillarCard pillar="operational" data={detail.row.operational} trainer={trainer} periodType={periodType} periodKey={periodKey} />
              <PillarCard pillar="quality"     data={detail.row.quality}     trainer={trainer} periodType={periodType} periodKey={periodKey} />
            </div>

            {/* Placement Rate — display-only, NOT in TPS */}
            <Card className="p-3 bg-amber-50/40 border-amber-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Placement Rate <span className="text-slate-400 font-normal normal-case">· for context only, not part of TPS</span></p>
                <p className="text-slate-600 text-xs leading-tight">% of students placed within 90 days of batch end. Will auto-fill from Zoho CRM in Phase 4.</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold tabular-nums text-amber-800">{detail.row.placement_display?.raw_pct != null ? `${detail.row.placement_display.raw_pct}%` : '—'}</p>
                <p className="text-[9px] text-slate-400 italic">{detail.row.placement_display?.raw_pct == null ? 'not entered' : 'manual entry'}</p>
              </div>
            </Card>

            {/* Trend */}
            <Card className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Recent Period Trend</p>
              {detail.trend?.length > 0 ? (
                <div className="flex items-end justify-between gap-3">
                  {detail.trend.map((t, i) => {
                    const v = t.tps ?? 0;
                    const prev = i > 0 ? (detail.trend[i - 1].tps ?? 0) : v;
                    const diff = v - prev;
                    const Trend = i === 0 ? Minus : diff > 1 ? TrendingUp : diff < -1 ? TrendingDown : Minus;
                    const color = i === 0 ? 'text-slate-400' : diff > 1 ? 'text-emerald-600' : diff < -1 ? 'text-rose-600' : 'text-slate-400';
                    return (
                      <div key={t.period_key} className="flex-1 text-center">
                        <div className="h-20 bg-slate-100 rounded relative mb-1 overflow-hidden">
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-violet-500 to-violet-300"
                               style={{ height: `${Math.min(v, 100)}%` }} />
                        </div>
                        <p className="text-xs font-semibold tabular-nums">{t.tps === null ? '—' : Math.round(t.tps)}</p>
                        <p className="text-[10px] text-slate-500">{t.period_key}</p>
                        <p className="text-[9px] text-slate-400">{t.coverage_pct}% cov</p>
                        {i > 0 && <Trend className={`w-3 h-3 mx-auto mt-0.5 ${color}`} />}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[11px] text-slate-500 italic">
                  <Info className="w-3.5 h-3.5 text-slate-400" />
                  Trend will appear once we have at least one fully-supported period (Apr 2026 onwards).
                </div>
              )}
              {detail.trend?.length > 0 && detail.trend.length < 3 && (
                <p className="text-[10px] text-slate-400 italic mt-2">Showing {detail.trend.length} period{detail.trend.length === 1 ? '' : 's'} — the scorecard is live from April 2026, so older periods aren't tracked.</p>
              )}
            </Card>

            {/* Manager input form */}
            <ManagerInputForm trainer={trainer} periodType={periodType} periodKey={periodKey} onSaved={() => onSaved?.()} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// Set of components that have a source-row drill-down. Quality components
// (TDQ/SFS/RAS) and placement are manual entries so don't need it.
const COMPONENTS_WITH_SOURCE = new Set([
  'workload', 'completion', 'consistency', 'versatility', 'tenure_bonus',
  'working_day', 'attendance', 'cct',
]);

function HighlightCard({ tone, Icon, label, pick, suffix, onClick }) {
  const toneMap = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    rose:    'bg-rose-50 border-rose-200 text-rose-700',
    violet:  'bg-violet-50 border-violet-200 text-violet-700',
    amber:   'bg-amber-50 border-amber-200 text-amber-700',
  };
  const display = pick?.trainer_name?.split('.')[0] || '—';
  const clickable = pick?.trainer_id;
  return (
    <button
      type="button"
      onClick={() => clickable && onClick(pick.trainer_id)}
      disabled={!clickable}
      className={`text-left p-3 rounded-lg border-2 ${toneMap[tone]} transition-all ${clickable ? 'hover:shadow-md cursor-pointer' : 'opacity-70'}`}
      data-testid={`highlight-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wider font-bold">{label}</p>
      </div>
      <p className="text-base font-bold text-slate-800 truncate">{display}</p>
      <p className="text-[11px] text-slate-600 mt-0.5">
        {pick?.value ?? '—'} {suffix}
      </p>
    </button>
  );
}

function PillarCard({ pillar, data, trainer, periodType, periodKey }) {
  const meta = PILLAR_META[pillar];
  const Icon = meta.Icon;
  const [popupKey, setPopupKey] = useState(null);
  const sub = pillar === 'operational' ? data?.submission_compliance : null;
  const subPctClass = sub?.value === null || sub?.value === undefined ? 'bg-slate-100 text-slate-600 border-slate-200'
    : sub.value >= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : sub.value >= 70 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  return (
    <Card className={`p-3 ${meta.bg} ${meta.border}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-4 h-4 ${meta.color}`} />
        <p className={`text-[11px] uppercase tracking-wider font-bold ${meta.color}`}>{meta.label}</p>
      </div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-2xl font-bold text-slate-800 tabular-nums">{data?.score ?? '—'}</span>
        {data?.score !== null && <span className="text-[10px] text-slate-500">/ 100</span>}
      </div>
      {sub && sub.value !== null && sub.value !== undefined && (
        <div
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border mb-2 ${subPctClass}`}
          title={`Submission Compliance — ${sub.raw?.submitted ?? 0} of ${sub.raw?.expected ?? 0} expected sessions submitted. Display-only (does NOT affect TPS).`}
          data-testid="submission-compliance-badge"
        >
          <ClipboardList className="w-2.5 h-2.5" />
          Submission {sub.value}%
          <span className="opacity-70">· {sub.raw?.missing ?? 0} missing</span>
        </div>
      )}
      <div className="space-y-1">
        {Object.entries(data?.components || {}).map(([key, c]) => {
          const lbl = COMPONENT_LABELS[key];
          const display = lbl?.short || key.replace(/_/g, ' ');
          const drillable = COMPONENTS_WITH_SOURCE.has(key) && trainer?.trainer_id;
          return (
            <button
              key={key}
              type="button"
              onClick={() => drillable && setPopupKey(key)}
              disabled={!drillable}
              className={`flex items-center gap-1.5 text-[10px] w-full text-left px-1 py-1 rounded ${drillable ? 'hover:bg-white/70 cursor-pointer' : ''}`}
              title={lbl ? `${lbl.name} — ${lbl.help}${drillable ? '\n\nClick for source data.' : ''}` : key}
              data-testid={`pillar-component-${key}`}
            >
              <span className="w-24 text-slate-600 truncate font-medium flex items-center gap-1">
                {drillable && <Maximize2 className="w-2.5 h-2.5 opacity-50" />}
                {display}
              </span>
              <div className="flex-1 h-1.5 rounded bg-white/60 overflow-hidden">
                <div className={`h-full ${meta.color.replace('text-', 'bg-')}`} style={{ width: `${c.value === null ? 0 : Math.min(c.value, 100)}%` }} />
              </div>
              <span className="w-10 text-right tabular-nums text-slate-700 font-semibold">{c.value === null ? '—' : c.value}</span>
              <span className="w-6 text-right text-slate-400 text-[9px]" title={`Weight inside pillar: ${c.weight}%`}>{c.weight}%</span>
            </button>
          );
        })}
      </div>
      {popupKey && trainer?.trainer_id && (
        <ComponentSourceDialog
          trainerId={trainer.trainer_id}
          trainerName={trainer.trainer_name}
          componentKey={popupKey}
          periodType={periodType}
          periodKey={periodKey}
          onClose={() => setPopupKey(null)}
        />
      )}
    </Card>
  );
}

function ComponentSourceDialog({ trainerId, trainerName, componentKey, periodType, periodKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cctBatchId, setCctBatchId] = useState(null);
  const [attBatch, setAttBatch] = useState(null); // {batch_id, batch_name}
  const [excRow, setExcRow] = useState(null);     // row being marked excused (working_day only)
  const lbl = COMPONENT_LABELS[componentKey];
  const reload = () => {
    setLoading(true);
    api.get(`/training/new-scorecard/trainer/${trainerId}/component-source/${componentKey}`, {
      params: { period_type: periodType, period_key: periodKey, limit: 500 },
    })
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.detail || 'Could not load source data'))
      .finally(() => setLoading(false));
  };
  const removeException = async (excId) => {
    if (!excId) return;
    if (!window.confirm('Remove this Holiday / Leave excuse? The date will go back to counting as a missed session.')) return;
    try {
      await api.delete(`/training/new-scorecard/calendar-exceptions/${excId}`);
      toast.success('Excuse removed');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/training/new-scorecard/trainer/${trainerId}/component-source/${componentKey}`, {
      params: { period_type: periodType, period_key: periodKey, limit: 500 },
    })
      .then(r => { if (alive) { setData(r.data); setErr(null); } })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || 'Could not load source data'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [trainerId, componentKey, periodType, periodKey]);

  const downloadCsv = () => {
    if (!data?.rows?.length) return;
    const cols = data.columns || [];
    const headers = cols.map(c => `"${c.label}"`).join(',');
    const dataRows = data.rows.map(r => cols.map(c => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers, ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${trainerName || 'trainer'}_${componentKey}_${periodKey}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Colour-code the working_day status_label cell
  const wdPill = (label) => {
    if (!label) return 'bg-slate-100 text-slate-600 border-slate-200';
    if (label.startsWith('Held')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (label.startsWith('Excused')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (label.startsWith('Missed — attendance not submitted')) return 'bg-rose-100 text-rose-700 border-rose-200';
    if (label.startsWith('Missed')) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };
  // Render the value for a single cell: turn batch_name / batch_number into a
  // link if a batch_id sits on the row. For CCT, clicking opens an inline
  // syllabus popup. For Attendance, clicking opens a date-wise sessions popup.
  const renderCell = (col, row) => {
    const v = row[col.key];
    if (v === undefined || v === null || v === '') return '—';
    // For working_day, colour-code the status_label
    if (componentKey === 'working_day' && col.key === 'status_label') {
      return <span className={`px-1.5 py-0.5 rounded text-[10px] border ${wdPill(String(v))}`}>{v}</span>;
    }
    const isBatchLinkCol = (col.key === 'batch_name' || col.key === 'batch_number') && row.batch_id;
    if (isBatchLinkCol) {
      // Prefer batch_number for the search query (more unique); fall back to whatever cell was clicked
      const searchTerm = row.batch_number && row.batch_number !== '—' ? row.batch_number : (row.batch_name || v);
      if (componentKey === 'cct') {
        return (
          <button
            type="button"
            onClick={() => setCctBatchId(row.batch_id)}
            className="text-violet-600 hover:underline font-medium inline-flex items-center gap-1"
            data-testid={`cct-batch-link-${row.batch_id}`}
          >
            {v}<BookOpen className="w-3 h-3" />
          </button>
        );
      }
      if (componentKey === 'attendance') {
        return (
          <button
            type="button"
            onClick={() => setAttBatch({ batch_id: row.batch_id, batch_name: row.batch_name, batch_number: row.batch_number })}
            className="text-violet-600 hover:underline font-medium inline-flex items-center gap-1"
            data-testid={`attendance-batch-link-${row.batch_id}`}
          >
            {v}<Maximize2 className="w-3 h-3" />
          </button>
        );
      }
      return (
        <a
          href={`/training/batches?search=${encodeURIComponent(searchTerm)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-600 hover:underline font-medium inline-flex items-center gap-1"
          title="Open in Batch Management"
        >
          {v}<ExternalLink className="w-3 h-3" />
        </a>
      );
    }
    return String(v);
  };

  return (
    <>
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0" data-testid={`component-source-dialog-${componentKey}`}>
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-baseline gap-2 flex-wrap">
            <span>{lbl?.name || componentKey}</span>
            <span className="text-xs font-normal text-slate-500">{trainerName} · {periodKey}</span>
          </DialogTitle>
          {lbl?.help && <p className="text-xs text-slate-600 italic mt-1">{lbl.help}</p>}
        </DialogHeader>
        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {loading && <p className="text-sm text-slate-500 italic">Loading source rows…</p>}
          {err && <p className="text-sm text-rose-700">{err}</p>}
          {data && (
            <>
              {data.headline && <p className="text-xs text-slate-600 italic">{data.headline}</p>}
              {data.totals && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.totals).filter(([, v]) => v !== null && v !== undefined && !Array.isArray(v)).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-[11px] bg-slate-50">
                      <span className="text-slate-500 mr-1">{k.replace(/_/g, ' ')}:</span>
                      <strong className="text-slate-800">{String(v)}</strong>
                    </Badge>
                  ))}
                </div>
              )}
              {(data.rows || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic py-8 text-center">No source rows for this period.</p>
              ) : componentKey === 'working_day' ? (
                <WorkingDayDateGroups
                  rows={data.rows}
                  onMarkExcused={setExcRow}
                  onRemoveException={removeException}
                  pillClass={wdPill}
                  onMarkWholeDay={async (d, batches) => {
                    if (!window.confirm(`Mark ${d} as Holiday for every batch this trainer runs? This date will be removed from the expected-sessions denominator.`)) return;
                    try {
                      await api.post('/training/new-scorecard/calendar-exceptions', {
                        date: d,
                        type: 'holiday',
                        trainer_id: trainerId,
                        batch_id: null,
                        reason: `Whole-day excuse — ${batches.length} batches`,
                      });
                      toast.success('Whole day marked as Holiday');
                      reload();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed');
                    }
                  }}
                />
              ) : (
                <div className="overflow-x-auto rounded border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        {(data.columns || []).map(c => <th key={c.key} className="px-2 py-1.5 text-left font-bold text-slate-700 border-b">{c.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-none hover:bg-slate-50">
                          {(data.columns || []).map(c => (
                            <td key={c.key} className={`px-2 py-1.5 text-slate-700 ${c.type === 'number' ? 'text-right tabular-nums' : ''}`}>{renderCell(c, r)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-between items-center bg-slate-50">
          <p className="text-[10px] text-slate-500">Showing up to 500 rows.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data?.rows?.length} className="text-xs"><BookOpen className="w-3 h-3 mr-1" />Download CSV</Button>
            <Button size="sm" onClick={onClose} className="text-xs">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {cctBatchId && <BatchTopicCompletionDialog batchId={cctBatchId} onClose={() => setCctBatchId(null)} />}
    {attBatch && (
      <BatchAttendanceDialog
        trainerId={trainerId}
        trainerName={trainerName}
        batch={attBatch}
        periodType={periodType}
        periodKey={periodKey}
        onClose={() => setAttBatch(null)}
      />
    )}
    {excRow && (
      <MarkExcusedDialog
        trainerId={trainerId}
        row={excRow}
        onClose={() => setExcRow(null)}
        onSaved={() => { setExcRow(null); reload(); }}
      />
    )}
    </>
  );
}

const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDDMonYYYY(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${String(d).padStart(2, '0')}-${MONTHS_ABBR[m - 1]}-${y}`;
}
function dateWeekday(iso) {
  if (!iso) return '';
  const dt = new Date(`${iso}T00:00:00`);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dt.getDay()];
}

function WorkingDayDateGroups({ rows, onMarkExcused, onRemoveException, pillClass, onMarkWholeDay }) {
  const [open, setOpen] = useState({}); // {date: bool}
  // Group rows by date (sorted desc — most recent first)
  const groups = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const k = r.date || '';
      if (!m[k]) m[k] = [];
      m[k].push(r);
    }
    return Object.entries(m).sort((a, b) => (b[0] > a[0] ? 1 : -1));
  }, [rows]);
  const summary = (batches) => {
    const c = { Held: 0, Excused: 0, Missed: 0 };
    for (const b of batches) {
      const s = b.status_label || '';
      if (s.startsWith('Held')) c.Held++;
      else if (s.startsWith('Excused')) c.Excused++;
      else c.Missed++;
    }
    return c;
  };
  const toggleAll = (state) => {
    const next = {};
    if (state) for (const [d] of groups) next[d] = true;
    setOpen(next);
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] text-slate-500 px-1">
        <span>{groups.length} working day{groups.length === 1 ? '' : 's'} in this period (past dates only)</span>
        <div className="flex gap-1">
          <button type="button" onClick={() => toggleAll(true)} className="text-violet-600 hover:underline">Expand all</button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={() => toggleAll(false)} className="text-violet-600 hover:underline">Collapse all</button>
        </div>
      </div>
      <div className="rounded border border-slate-200 divide-y divide-slate-100">
        {groups.map(([d, batches]) => {
          const isOpen = !!open[d];
          const c = summary(batches);
          const allHeld = c.Missed === 0 && c.Held > 0;
          const allMissed = c.Missed === batches.length && batches.length > 0;
          const headerTone = allHeld ? 'bg-emerald-50/40' : allMissed ? 'bg-rose-50/40' : c.Excused === batches.length ? 'bg-blue-50/40' : 'bg-amber-50/40';
          return (
            <div key={d}>
              <button
                type="button"
                onClick={() => setOpen(o => ({ ...o, [d]: !o[d] }))}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left ${headerTone} hover:bg-violet-50/30`}
                data-testid={`wd-date-row-${d}`}
              >
                {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                <span className="font-mono text-xs font-bold text-slate-800 tabular-nums">{formatDDMonYYYY(d)}</span>
                <span className="text-[10px] text-slate-400">{dateWeekday(d)}</span>
                <span className="text-[10px] text-slate-500 ml-2">{batches.length} batch{batches.length === 1 ? '' : 'es'}</span>
                <span className="ml-auto flex items-center gap-1">
                  {c.Held > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold border border-emerald-200">✓ {c.Held} Held</span>}
                  {c.Excused > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold border border-blue-200">⛱ {c.Excused} Excused</span>}
                  {c.Missed > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold border border-rose-200">✗ {c.Missed} Missed</span>}
                  {allMissed && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onMarkWholeDay(d, batches); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onMarkWholeDay(d, batches); } }}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-violet-600 text-white hover:bg-violet-700 cursor-pointer"
                      data-testid={`wd-mark-whole-day-${d}`}
                      title="Mark this whole day as a Holiday / Centre Closed for every batch in one click"
                    >
                      🎉 Mark Whole Day Holiday
                    </span>
                  )}
                </span>
              </button>
              {isOpen && (
                <div className="px-3 py-2 bg-slate-50/40">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="text-left font-semibold py-1">Batch No.</th>
                        <th className="text-left font-semibold py-1">Batch</th>
                        <th className="text-left font-semibold py-1">Status</th>
                        <th className="text-right font-semibold py-1">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((r, i) => {
                        const isMissingSubmission = r.status_label && r.status_label.startsWith('Missed — attendance not submitted');
                        return (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="py-1.5 pr-2">
                              <a
                                href={`/training/batches?search=${encodeURIComponent(r.batch_number || r.batch_name || '')}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-violet-600 hover:underline font-mono text-[11px] inline-flex items-center gap-1"
                                title="Open in Batch Management"
                              >
                                {r.batch_number || '—'}<ExternalLink className="w-3 h-3" />
                              </a>
                            </td>
                            <td className="py-1.5 pr-2 text-slate-700">{r.batch_name || '—'}</td>
                            <td className="py-1.5 pr-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] border ${pillClass(r.status_label)}`}>{r.status_label}</span>
                            </td>
                            <td className="py-1.5 text-right">
                              {r.exception_id ? (
                                <button
                                  type="button"
                                  onClick={() => onRemoveException(r.exception_id)}
                                  className="text-[10px] text-rose-600 hover:underline"
                                  data-testid={`wd-remove-exc-${d}-${i}`}
                                >Undo Excuse</button>
                              ) : isMissingSubmission ? (
                                <button
                                  type="button"
                                  onClick={() => onMarkExcused(r)}
                                  className="text-[10px] text-violet-600 hover:underline"
                                  data-testid={`wd-mark-exc-${d}-${i}`}
                                >Mark Excused</button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MarkExcusedDialog({ trainerId, row, onClose, onSaved }) {
  const [type, setType] = useState('holiday');
  const [scope, setScope] = useState('this-batch'); // this-batch | all-batches
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const TYPES = [
    { value: 'holiday', label: '🎉 Public Holiday' },
    { value: 'centre_closed', label: '🏢 Centre Closed' },
    { value: 'trainer_leave_approved', label: '🛌 Approved Trainer Leave' },
  ];
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/training/new-scorecard/calendar-exceptions', {
        date: row.date,
        type,
        trainer_id: trainerId,
        batch_id: scope === 'this-batch' ? row.batch_id : null,
        reason: reason || null,
      });
      toast.success('Date excused');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md p-0" data-testid="mark-excused-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle>Mark date as Excused</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-mono">{row.date}</span> · <span>{row.batch_name}</span>
          </p>
          <p className="text-[11px] text-slate-500 italic">This date will be removed from the trainer's expected-sessions denominator. Use this for Holidays, Centre Closures, or Approved Leaves only.</p>
        </DialogHeader>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Reason type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-9 text-xs" data-testid="exc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Apply to</label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 text-xs" data-testid="exc-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this-batch">Just this batch ({row.batch_name})</SelectItem>
                <SelectItem value="all-batches">All this trainer's batches on this date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Note (optional)</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Diwali, Building lift maintenance" className="h-9 text-xs" data-testid="exc-reason" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="text-xs bg-violet-600 hover:bg-violet-700" data-testid="exc-save">
            {saving ? 'Saving…' : 'Mark Excused'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function BatchAttendanceDialog({ trainerId, trainerName, batch, periodType, periodKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({}); // {date: bool}
  const [showAbsenteeRollup, setShowAbsenteeRollup] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/training/new-scorecard/trainer/${trainerId}/attendance/${batch.batch_id}`, {
      params: { period_type: periodType, period_key: periodKey, limit: 500 },
    })
      .then(r => { if (alive) { setData(r.data); setErr(null); } })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || 'Could not load attendance'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [trainerId, batch.batch_id, periodType, periodKey]);

  const t = data?.totals;
  const rateClass = (r) => r === null || r === undefined ? 'bg-slate-100 text-slate-500 border-slate-200'
    : r >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : r >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  const statusPill = (s) => {
    if (s === 'present') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s === 'late')    return 'bg-amber-100 text-amber-700 border-amber-200';
    if (s === 'absent')  return 'bg-rose-100 text-rose-700 border-rose-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };
  const toggleAll = (open) => {
    if (!data) return;
    const next = {};
    if (open) data.rows.forEach(r => { next[r.date] = true; });
    setExpanded(next);
  };

  // Top repeat-absentees (anyone with at least 1 absent)
  const repeatAbsentees = (data?.student_summary || []).filter(s => s.absent > 0);

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col p-0" data-testid={`attendance-batch-dialog-${batch.batch_id}`}>
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-baseline gap-2 flex-wrap">
            <Users className="w-4 h-4 text-violet-600" />
            <span className="px-1.5 py-0.5 bg-violet-100 text-violet-800 rounded font-mono text-xs">{batch.batch_number || '—'}</span>
            <span>{batch.batch_name || 'Batch'}</span>
            <span className="text-xs font-normal text-slate-500">· {trainerName} · {periodKey}</span>
            {t?.rate !== null && t?.rate !== undefined && (
              <Badge className={`ml-auto text-[11px] ${rateClass(t.rate)}`} variant="outline">
                {t.sessions} sessions · {t.rate}% present
              </Badge>
            )}
          </DialogTitle>
          <p className="text-xs text-slate-500 italic mt-1">Click any date row to reveal each student's status. Repeat absentees are summarised at the top.</p>
        </DialogHeader>
        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          {loading && <p className="text-sm text-slate-500 italic">Loading attendance…</p>}
          {err && <p className="text-sm text-rose-700">{err}</p>}
          {data && (data.rows || []).length === 0 && (
            <p className="text-sm text-slate-400 italic py-8 text-center">No held sessions for this batch in this period.</p>
          )}

          {/* Repeat-absentee rollup */}
          {data && repeatAbsentees.length > 0 && (
            <Card className="p-3 border-rose-200 bg-rose-50/40" data-testid="attendance-repeat-absentees">
              <button
                type="button"
                onClick={() => setShowAbsenteeRollup(o => !o)}
                className="flex items-center gap-2 w-full text-left"
              >
                {showAbsenteeRollup ? <ChevronDown className="w-3.5 h-3.5 text-rose-600" /> : <ChevronRight className="w-3.5 h-3.5 text-rose-600" />}
                <UserX className="w-3.5 h-3.5 text-rose-600" />
                <p className="text-[11px] uppercase tracking-wider font-bold text-rose-700">Repeat absentees</p>
                <Badge variant="outline" className="text-[10px] bg-rose-100 text-rose-700 border-rose-200">
                  {repeatAbsentees.length} student{repeatAbsentees.length === 1 ? '' : 's'}
                </Badge>
                <span className="ml-auto text-[10px] text-slate-500">across {t?.sessions || 0} sessions</span>
              </button>
              {showAbsenteeRollup && (
                <div className="mt-2 overflow-x-auto rounded border border-rose-100">
                  <table className="w-full text-xs bg-white">
                    <thead className="bg-rose-50 text-rose-800">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-semibold">Student</th>
                        <th className="px-2 py-1.5 text-left font-semibold">Enrollment</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Present</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Late</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Absent</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Attendance %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repeatAbsentees.map((s, i) => (
                        <tr key={s.student_id || s.name || i} className="border-t border-rose-100">
                          <td className="px-2 py-1.5 text-slate-800 font-medium">{s.name}</td>
                          <td className="px-2 py-1.5 text-slate-500 font-mono">{s.enrollment_no || '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{s.present}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{s.late}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-rose-700 font-bold">{s.absent}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] border ${rateClass(s.attendance_pct)}`}>{s.attendance_pct === null ? '—' : `${s.attendance_pct}%`}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {data && (data.rows || []).length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Date-wise sessions</p>
                <div className="flex gap-1">
                  <button type="button" onClick={() => toggleAll(true)} className="text-[10px] text-violet-600 hover:underline">Expand all</button>
                  <span className="text-[10px] text-slate-300">·</span>
                  <button type="button" onClick={() => toggleAll(false)} className="text-[10px] text-violet-600 hover:underline">Collapse all</button>
                </div>
              </div>
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-bold text-slate-700 border-b w-6"></th>
                      <th className="px-2 py-1.5 text-left font-bold text-slate-700 border-b">Date</th>
                      <th className="px-2 py-1.5 text-right font-bold text-slate-700 border-b">Present</th>
                      <th className="px-2 py-1.5 text-right font-bold text-slate-700 border-b">Late</th>
                      <th className="px-2 py-1.5 text-right font-bold text-slate-700 border-b">Absent</th>
                      <th className="px-2 py-1.5 text-right font-bold text-slate-700 border-b">Rate %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => {
                      const open = !!expanded[r.date];
                      return (
                        <Fragment key={r.date || i}>
                          <tr
                            className="border-b border-slate-100 hover:bg-violet-50/40 cursor-pointer"
                            onClick={() => setExpanded(e => ({ ...e, [r.date]: !e[r.date] }))}
                            data-testid={`attendance-date-row-${r.date}`}
                          >
                            <td className="px-2 py-1.5 text-slate-400">
                              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </td>
                            <td className="px-2 py-1.5 text-slate-700 font-mono">{r.date}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{r.present}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{r.late}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{r.absent}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {r.rate === null ? '—' : (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${rateClass(r.rate)}`}>{r.rate}%</span>
                              )}
                            </td>
                          </tr>
                          {open && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={6} className="px-3 py-2">
                                {(r.students || []).length === 0 ? (
                                  <p className="text-[11px] italic text-slate-500">No student records for this session.</p>
                                ) : (
                                  <div className="flex flex-wrap gap-1.5" data-testid={`attendance-students-${r.date}`}>
                                    {r.students.map((s, j) => (
                                      <span
                                        key={s.student_id || s.name || j}
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${statusPill(s.status)}`}
                                        title={`${s.name}${s.enrollment_no ? ' · ' + s.enrollment_no : ''} — ${s.status || 'unmarked'}`}
                                      >
                                        <span className="font-medium">{s.name}</span>
                                        {s.enrollment_no && <span className="text-[9px] font-mono opacity-70">#{s.enrollment_no}</span>}
                                        <span className="uppercase text-[9px] font-bold tracking-wider opacity-80">{s.status || '—'}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  {t && (
                    <tfoot className="bg-slate-50 border-t">
                      <tr>
                        <td className="px-2 py-1.5"></td>
                        <td className="px-2 py-1.5 font-semibold text-slate-700">Total ({t.sessions} sessions)</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-700">{t.present}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-amber-700">{t.late}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-rose-700">{t.absent}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold">{t.rate === null ? '—' : `${t.rate}%`}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end items-center bg-slate-50">
          <Button size="sm" onClick={onClose} className="text-xs">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ManagerInputForm({ trainer, periodType, periodKey, onSaved }) {
  const [tdq, setTdq] = useState('');
  const [sfs, setSfs] = useState('');
  const [ras, setRas] = useState('');
  const [placement, setPlacement] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!trainer) return;
    api.get('/training/new-scorecard/manual-input', {
      params: { trainer_name: trainer.trainer_name, period_type: periodType, period_key: periodKey },
    }).then(r => {
      const row = r.data?.rows?.[0];
      setTdq(row?.tdq ?? '');
      setSfs(row?.sfs ?? '');
      setRas(row?.ras ?? '');
      setPlacement(row?.placement_rate ?? '');
      setNotes(row?.notes ?? '');
    }).catch(() => { /* ignore */ });
  }, [trainer, periodType, periodKey]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/training/new-scorecard/manual-input', {
        trainer_id: trainer.trainer_id,
        trainer_name: trainer.trainer_name,
        period_type: periodType,
        period_key: periodKey,
        tdq: tdq === '' ? null : Number(tdq),
        sfs: sfs === '' ? null : Number(sfs),
        ras: ras === '' ? null : Number(ras),
        placement_rate: placement === '' ? null : Number(placement),
        notes: notes || null,
      });
      toast.success('Saved — Quality pillar & Placement updated');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear manual scores for this trainer/period?')) return;
    try {
      await api.delete('/training/new-scorecard/manual-input', {
        params: { trainer_name: trainer.trainer_name, period_type: periodType, period_key: periodKey },
      });
      setTdq(''); setSfs(''); setRas(''); setPlacement(''); setNotes('');
      toast.success('Cleared');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed');
    }
  };

  return (
    <div className="space-y-3">
      <Card className="p-4 bg-rose-50/30 border-rose-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Manager Inputs · Quality Pillar
          </p>
          <span className="text-[9px] text-slate-400 italic">Admin / Manager only</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-slate-700 font-semibold block mb-0.5" title={COMPONENT_LABELS.tdq.help}>
              TDQ — Delivery Quality <span className="text-slate-400 font-normal">(0–5)</span>
            </label>
            <Input type="number" step="0.1" min="0" max="5" value={tdq} onChange={e => setTdq(e.target.value)} placeholder="e.g., 4.2" className="h-8 text-xs" data-testid="new-scorecard-tdq" />
            <p className="text-[9px] text-slate-500 mt-0.5 leading-tight">How well they deliver the session</p>
          </div>
          <div>
            <label className="text-[10px] text-slate-700 font-semibold block mb-0.5" title={COMPONENT_LABELS.sfs.help}>
              SFS — Student Feedback <span className="text-slate-400 font-normal">(0–5)</span>
            </label>
            <Input type="number" step="0.1" min="0" max="5" value={sfs} onChange={e => setSfs(e.target.value)} placeholder="e.g., 4.0" className="h-8 text-xs" data-testid="new-scorecard-sfs" />
            <p className="text-[9px] text-slate-500 mt-0.5 leading-tight">Student rating (manual until form launches)</p>
          </div>
          <div>
            <label className="text-[10px] text-slate-700 font-semibold block mb-0.5" title={COMPONENT_LABELS.ras.help}>
              RAS — Assessment Score <span className="text-slate-400 font-normal">(0–100 %)</span>
            </label>
            <Input type="number" step="1" min="0" max="100" value={ras} onChange={e => setRas(e.target.value)} placeholder="e.g., 78" className="h-8 text-xs" data-testid="new-scorecard-ras" />
            <p className="text-[9px] text-slate-500 mt-0.5 leading-tight">Avg test/assignment score under this trainer</p>
          </div>
        </div>
      </Card>

      <Card className="p-3 bg-amber-50/30 border-amber-100">
        <div className="flex items-center gap-3">
          <Trophy className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Placement Rate · Display only <span className="text-slate-400 font-normal normal-case">— not part of TPS</span></p>
            <p className="text-[9px] text-slate-500">% of students placed within 90 days. Zoho CRM auto-fill in Phase 4.</p>
          </div>
          <Input type="number" step="1" min="0" max="100" value={placement} onChange={e => setPlacement(e.target.value)} placeholder="e.g., 72" className="h-8 text-xs w-28" data-testid="new-scorecard-placement" />
        </div>
      </Card>

      <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes (visible in audit trail)" className="h-8 text-xs" />
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} size="sm" className="bg-rose-600 hover:bg-rose-700 flex-1" data-testid="new-scorecard-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={clear} variant="outline" size="sm" data-testid="new-scorecard-clear"><X className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}
